import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Configure,
  InstantSearch,
  Stats,
  useInfiniteHits,
  useInstantSearch,
} from 'react-instantsearch'
import { ALGOLIA_INDEX_NAME, searchClient } from '../../infrastructure/algolia/algoliaConfig'
import { useAssetPanelStore } from '../../stores/assetPanelStore'
import { useCanvasStore } from '../../stores/canvasStore'
import { useIntlayer, useLocale } from 'react-intlayer'
import { AssetLibraryAssistant } from './AssetLibraryAssistant'
import { useQueryAssets } from '../hooks/useQueryAssets'
import { ensureSvgAssetUrl, getAssetUrl } from '../../infrastructure/api/assetsApi'

import type {
  Asset,
  AssetAlbum,
  AssetType as AssetTypeEnum,
  AssetsResponse,
} from '../../domain/canvas/value-objects/asset'
import * as Sentry from '@sentry/react'

// Hit component for rendering each search result
type HitProps = {
  hit: {
    objectID: string
    assetId: string
    folderId: string
    type: 'print' | 'engrave'
    filename: string
    name: {
      nl: string
      fr: string
      de: string
      en: string
    }
    folderName: {
      nl: string
      fr: string
      de: string
      en: string
    }
    imageUrl: string
    dimensions?: {
      width: number
      height: number
    }
    fileSize?: number
    createdAt?: string
    indexedAt?: string
    _searchableContent?: string
    _highlightResult?: unknown
  }
}

const Hit = ({ hit }: HitProps) => {
  const { locale } = useLocale()
  const closePanel = useAssetPanelStore((state) => state.closePanel)
  const addAssetElement = useCanvasStore((state) => state.addAssetElement)

  const handleSelectAsset = useCallback(async () => {
    // Use the imageUrl directly from Algolia, but ensure we use SVG for canvas rendering
    const assetUrl = ensureSvgAssetUrl(hit.imageUrl)

    // Get localized name based on current locale
    const name = hit.name[locale as keyof typeof hit.name] || hit.name.nl || hit.filename

    // Use the actual assetId from the index
    const { assetId } = hit

    if (!assetId) {
      const errorMessage = `Asset ID not found for Algolia hit`
      console.error(errorMessage, {
        hit,
        filename: hit.filename,
        folder: hit.folderName,
        type: hit.type,
        objectID: hit.objectID,
      })

      // Send to Sentry with context
      Sentry.captureException(new Error(errorMessage), {
        tags: {
          component: 'AlgoliaAssetPanel',
          assetType: hit.type,
        },
        extra: {
          hit,
          filename: hit.filename,
          folderName: hit.folderName,
          type: hit.type,
          objectID: hit.objectID,
          locale,
        },
      })

      // Show user-friendly message without alert
      console.warn('Asset cannot be added - missing ID. This has been reported.')
      return
    }

    addAssetElement({
      src: assetUrl,
      name,
      assetId,
      assetType: hit.type,
    })
    closePanel()
  }, [hit, locale, addAssetElement, closePanel])

  return (
    <button
      onClick={handleSelectAsset}
      className="aspect-square bg-gray-100 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors p-2 group relative"
      style={{ contain: 'layout style paint' }}
    >
      <div className="absolute top-1 right-1 flex gap-1 z-10">
        <div
          className={`text-white text-xs px-1.5 py-0.5 rounded ${
            hit.type === 'engrave' ? 'bg-black' : 'bg-green-700'
          }`}
        >
          {hit.type === 'engrave' ? 'E' : 'P'}
        </div>
      </div>

      <img
        src={hit.imageUrl}
        alt={hit.name[locale as keyof typeof hit.name] || hit.name.nl}
        title={`${hit.name[locale as keyof typeof hit.name] || hit.name.nl} - ${hit.folderName[locale as keyof typeof hit.folderName] || hit.folderName.nl}`}
        loading="lazy"
        decoding="async"
        className="w-full h-full object-contain"
        style={{ willChange: 'auto' }}
        onError={(e) => {
          console.error('Image failed to load:', hit.imageUrl)
          // Set a fallback image
          const target = e.target as HTMLImageElement
          target.src =
            'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect width="100" height="100" fill="%23ddd"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%23999" font-family="sans-serif" font-size="16"%3E?%3C/text%3E%3C/svg%3E'
        }}
      />
    </button>
  )
}

// Category browser component - Uses traditional API like the old panel
const CategoryBrowser = ({
  assets,
  assetType,
  onSelectCategory,
}: {
  assets: AssetsResponse | null
  assetType: AssetTypeEnum
  onSelectCategory: (album: AssetAlbum, albumType: AssetTypeEnum) => void
}) => {
  const intl = useIntlayer('assetPanel')
  const { locale } = useLocale()

  if (!assets) return null

  // Same logic as the old panel - show categories based on design type
  const assetTypesToShow: AssetTypeEnum[] =
    assetType === 'print' ? ['print', 'engrave'] : ['engrave']

  // Group albums by folder name (combine same-named albums from different types)
  const albumMap = new Map<
    string,
    {
      album: AssetAlbum
      sourceTypes: AssetTypeEnum[]
      totalAssets: number
      firstAsset: { sourceType: AssetTypeEnum; filename: string; thumbnail: string } | null
    }
  >()

  assetTypesToShow.forEach((currentType) => {
    const albums = assets[currentType] || []
    albums.forEach((album: AssetAlbum) => {
      const existing = albumMap.get(album.folderName)

      if (existing) {
        existing.sourceTypes.push(currentType)
        existing.totalAssets += album.assets.length
        if (!existing.firstAsset && album.assets.length > 0) {
          existing.firstAsset = {
            sourceType: currentType,
            filename: album.assets[0].filename,
            thumbnail: album.assets[0].thumbnail,
          }
        }
      } else {
        albumMap.set(album.folderName, {
          album,
          sourceTypes: [currentType],
          totalAssets: album.assets.length,
          firstAsset:
            album.assets.length > 0
              ? {
                  sourceType: currentType,
                  filename: album.assets[0].filename,
                  thumbnail: album.assets[0].thumbnail,
                }
              : null,
        })
      }
    })
  })

  const combinedAlbums = Array.from(albumMap.values()).filter(({ totalAssets }) => totalAssets > 0)

  return (
    <div
      className="flex-1 overflow-y-auto overscroll-contain touch-pan-y min-h-0"
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
        {combinedAlbums.map(({ album, sourceTypes, totalAssets, firstAsset }) => (
          <button
            key={album.folderName}
            onClick={() => {
              // Create a combined album with all assets from all types
              const combinedAlbum: AssetAlbum = {
                id: album.id,
                folderName: album.folderName,
                translations: album.translations,
                assets: [],
              }

              sourceTypes.forEach((sourceType) => {
                const sourceAlbum = assets[sourceType]?.find(
                  (a: AssetAlbum) => a.folderName === album.folderName,
                )
                if (sourceAlbum) {
                  combinedAlbum.assets.push(
                    ...sourceAlbum.assets.map((asset) => ({
                      ...asset,
                      _sourceType: sourceType,
                      _sourceFolderId: sourceAlbum.id, // Store the correct folder ID for this asset type
                    })),
                  )
                }
              })

              onSelectCategory(combinedAlbum, sourceTypes[0])
            }}
            className="aspect-square bg-gray-100 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors p-4 flex flex-col items-center justify-center group"
            style={{ contain: 'layout style paint' }}
          >
            {firstAsset && (
              <img
                src={getAssetUrl(firstAsset.sourceType, album.folderName, firstAsset.thumbnail)}
                alt={
                  album.translations[locale as keyof typeof album.translations] ||
                  album.translations.nl ||
                  album.folderName
                }
                loading="lazy"
                decoding="async"
                className="object-contain mb-2"
                style={{ width: '150px', height: '150px', willChange: 'auto' }}
              />
            )}
            <span className="text-sm font-medium text-gray-700 text-center">
              {album.translations[locale as keyof typeof album.translations] ||
                album.translations.nl ||
                album.folderName}
            </span>
            <span className="text-xs text-gray-500">
              {totalAssets} {(intl.assets as any)?.key || 'afbeeldingen'}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

// Component to show assets in a selected category (using traditional API)
const CategoryAssetView = ({
  album,
  albumType,
  onBack,
}: {
  album: AssetAlbum & {
    assets: Array<Asset & { _sourceType?: AssetTypeEnum; _sourceFolderId?: string }>
  }
  albumType: AssetTypeEnum
  onBack: () => void
}) => {
  const intl = useIntlayer('assetPanel')
  const { locale } = useLocale()
  const addAssetElement = useCanvasStore((state) => state.addAssetElement)
  const closePanel = useAssetPanelStore((state) => state.closePanel)

  return (
    <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
      <button
        onClick={onBack}
        className="mb-3 flex items-center text-blue-600 hover:text-blue-800 transition-colors"
      >
        {(intl.backToAlbums as any)?.key || 'Back to albums'}
      </button>
      <h4 className="font-semibold mb-4">
        {album.translations[locale as keyof typeof album.translations] ||
          album.translations.nl ||
          album.folderName}
      </h4>
      <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-8 xl:grid-cols-10 gap-3">
        {album.assets.map((asset: any, index: number) => {
          const sourceType = asset._sourceType || albumType

          return (
            <button
              key={`${sourceType}-${asset.filename}-${index}`}
              onClick={() => {
                // This uses the traditional approach - we need to find assetId
                void (async () => {
                  // Use the correct folder ID based on the asset's source type
                  const correctFolderId = asset._sourceFolderId || album.id || ''

                  // Use the correct folder ID for finding the asset

                  const { findAssetId } = await import(
                    '../../infrastructure/services/assetIdMapper'
                  )
                  const assetId = await findAssetId(correctFolderId, asset.filename)

                  if (!assetId) {
                    const errorMessage = `Asset ID not found in CategoryAssetView`
                    console.error(errorMessage, {
                      filename: asset.filename,
                      albumId: album.id,
                      albumFolder: album.folderName,
                      sourceType,
                      assetData: asset,
                    })

                    // Send to Sentry with detailed context
                    Sentry.captureException(new Error(errorMessage), {
                      tags: {
                        component: 'CategoryAssetView',
                        assetType: sourceType,
                        albumId: album.id || 'unknown',
                      },
                      extra: {
                        filename: asset.filename,
                        albumId: album.id,
                        albumFolder: album.folderName,
                        sourceType,
                        asset,
                        translations: asset.translations,
                        locale,
                      },
                    })

                    // Show user-friendly message without alert
                    console.warn(
                      `Asset '${asset.filename}' cannot be added - missing ID. This has been reported.`,
                    )
                    return
                  }

                  addAssetElement({
                    src: ensureSvgAssetUrl(
                      getAssetUrl(sourceType, album.folderName, asset.filename),
                    ),
                    name:
                      asset.translations?.[locale as keyof typeof asset.translations] ||
                      asset.translations?.nl ||
                      asset.filename,
                    assetId,
                    assetType: sourceType,
                  })
                  closePanel()
                })()
              }}
              className="aspect-square bg-gray-100 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors p-2 group relative"
              style={{ contain: 'layout style paint' }}
            >
              <img
                src={getAssetUrl(sourceType, album.folderName, asset.thumbnail)}
                alt={
                  asset.translations?.[locale as keyof typeof asset.translations] ||
                  asset.translations?.nl ||
                  asset.filename
                }
                loading="lazy"
                decoding="async"
                className="w-full h-full object-contain"
                style={{ willChange: 'auto' }}
              />
            </button>
          )
        })}
      </div>
    </div>
  )
}

// Custom infinite scroll component that loads on scroll
const InfiniteScrollHits = () => {
  const { hits, isLastPage, showMore } = useInfiniteHits()
  const sentinelRef = useRef<HTMLDivElement>(null)
  const isLoadingRef = useRef(false)
  const lastHitCountRef = useRef(0)
  const intl = useIntlayer('assetPanel')

  // Auto-load next page when sentinel is visible
  useEffect(() => {
    if (!sentinelRef.current || isLastPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        // Only load if visible and not already loading
        if (entry.isIntersecting && !isLoadingRef.current && !isLastPage) {
          isLoadingRef.current = true
          showMore()
        }
      },
      {
        root: null, // Use viewport as root
        rootMargin: '200px', // Trigger 200px before the sentinel becomes visible
        threshold: 0,
      },
    )

    observer.observe(sentinelRef.current)

    return () => {
      observer.disconnect()
    }
  }, [isLastPage, showMore])

  // Reset loading state when hits change (new page loaded)
  useEffect(() => {
    if (hits.length > lastHitCountRef.current) {
      lastHitCountRef.current = hits.length
      // Reset loading flag after hits increase
      isLoadingRef.current = false
    }
  }, [hits.length])

  return (
    <>
      <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-8 xl:grid-cols-10 gap-3">
        {hits.map((hit) => (
          <Hit key={hit.objectID} hit={hit as any} />
        ))}
      </div>

      {/* Sentinel element for intersection observer */}
      {!isLastPage && (
        <div ref={sentinelRef} className="col-span-full mt-4 flex justify-center py-4">
          <div className="text-gray-500 text-sm">
            {typeof intl.loadingAssets === 'string' ? intl.loadingAssets : 'Laden...'}
          </div>
        </div>
      )}
    </>
  )
}

// Results component for Algolia search
const AlgoliaSearchResults = ({
  searchQuery,
  selectedAlbum,
  onClearAlbum,
}: {
  searchQuery: string
  selectedAlbum: AssetAlbum | null
  onClearAlbum: () => void
}) => {
  const { results, error } = useInstantSearch()
  const intl = useIntlayer('assetPanel')
  const { locale } = useLocale()

  if (error) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="text-red-500 p-4">Error loading results: {error.message}</div>
      </div>
    )
  }

  if (!results || !searchQuery) {
    return null
  }

  return (
    <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
      {/* Show when searching within a category */}
      {selectedAlbum && (
        <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded flex items-center justify-between">
          <span className="text-sm text-blue-700">
            Searching in:{' '}
            {selectedAlbum.translations[locale as keyof typeof selectedAlbum.translations] ||
              selectedAlbum.folderName}
          </span>
          <button
            onClick={onClearAlbum}
            className="text-sm text-blue-600 hover:text-blue-800 underline"
          >
            Search all categories
          </button>
        </div>
      )}

      <div className="mb-3 text-sm text-gray-600">
        <Stats
          translations={{
            rootElementText: ({ nbHits, processingTimeMS }) => {
              const oneResultText =
                typeof intl.oneResult === 'string' ? intl.oneResult : 'resultaat'
              const resultsText = typeof intl.results === 'string' ? intl.results : 'resultaten'
              const foundForText =
                typeof intl.foundFor === 'string' ? intl.foundFor : 'gevonden voor'
              const resultText = nbHits === 1 ? oneResultText : resultsText

              return `${nbHits.toLocaleString()} ${resultText} ${foundForText} "${searchQuery}" (${processingTimeMS}ms)`
            },
          }}
        />
      </div>

      <InfiniteScrollHits />
    </div>
  )
}

export const AlgoliaAssetPanel = () => {
  const intl = useIntlayer('assetPanel')
  const [isMobile, setIsMobile] = useState(false)
  const [showAssistant, setShowAssistant] = useState(false)
  const [selectedAlbum, setSelectedAlbum] = useState<AssetAlbum | null>(null)
  const [selectedAlbumType, setSelectedAlbumType] = useState<AssetTypeEnum | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  // Asset panel store
  const isOpen = useAssetPanelStore((state) => state.isOpen)
  const assetType = useAssetPanelStore((state) => state.assetType)
  const hasSeenAssistant = useAssetPanelStore((state) => state.hasSeenAssistant)
  const closePanel = useAssetPanelStore((state) => state.closePanel)
  const dismissAssistant = useAssetPanelStore((state) => state.dismissAssistant)

  // Load assets from traditional API
  const { assets, isLoading, error } = useQueryAssets()

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const panelClasses = isMobile
    ? `fixed inset-0 bg-white z-50 transform transition-all duration-300 ease-in-out flex flex-col ${
        isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'
      }`
    : `fixed inset-0 bg-white border-l border-gray-300 shadow-lg z-40 transform transition-all duration-300 ease-in-out ${
        isOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'
      }`

  // Determine filters based on design type
  const filters = assetType === 'print' ? 'type:print OR type:engrave' : 'type:engrave'

  return (
    <div className={panelClasses} data-asset-panel onClick={(e) => e.stopPropagation()}>
      <div className={isMobile ? 'p-4 flex flex-col h-full' : 'p-4 h-full flex flex-col'}>
        {/* Header */}
        <div className="flex justify-between items-center mb-4 border-b border-gray-200 pb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {(intl.assetLibrary as any)?.key || 'Asset Library'}
            </h3>
            <p className="text-sm text-gray-500">
              {assetType === 'print'
                ? (intl.printAssets as any)?.key || 'Print Assets'
                : (intl.engraveAssets as any)?.key || 'Engrave Assets'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAssistant(true)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              title={(intl.showAssistantHelp as any)?.key || 'Show help'}
            >
              <svg
                className="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </button>
            <button
              onClick={closePanel}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              title={(intl.closePanel as any)?.key || 'Close panel'}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Search Box - Always visible */}
        <input
          type="search"
          placeholder={(intl.searchAssets as any)?.key || 'Search assets...'}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value)
            // Don't clear album selection - we want to search within the category
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
        />

        {/* Content Area */}
        {searchQuery ? (
          // When searching, use Algolia
          <InstantSearch searchClient={searchClient} indexName={ALGOLIA_INDEX_NAME}>
            <Configure
              query={searchQuery}
              filters={(() => {
                if (selectedAlbum) {
                  // Much simpler: use folderId for filtering
                  // The combined album might have assets from multiple folders (print/engrave)
                  // So we need to collect all folder IDs
                  const folderIds: string[] = []

                  // Get all unique folder IDs from the assets in this album
                  if (selectedAlbum.assets && selectedAlbum.assets.length > 0) {
                    // For combined albums, each asset might have a _sourceFolderId
                    const uniqueFolderIds = new Set<string>()

                    selectedAlbum.assets.forEach((asset: any) => {
                      if (asset._sourceFolderId) {
                        uniqueFolderIds.add(asset._sourceFolderId)
                      }
                    })

                    // If we found source folder IDs, use those
                    if (uniqueFolderIds.size > 0) {
                      folderIds.push(...Array.from(uniqueFolderIds))
                    } else if (selectedAlbum.id) {
                      // Otherwise fall back to the album's own ID
                      folderIds.push(selectedAlbum.id)
                    }
                  } else if (selectedAlbum.id) {
                    // No assets loaded yet, just use the album ID
                    folderIds.push(selectedAlbum.id)
                  }

                  // Build the filter using folderId
                  const folderFilter =
                    folderIds.length > 0
                      ? `(${folderIds.map((id) => `folderId:"${id}"`).join(' OR ')})`
                      : ''

                  const finalFilter = folderFilter ? `(${filters}) AND ${folderFilter}` : filters

                  // Filter by folderId(s) - much simpler and more reliable than folder names

                  return finalFilter
                }
                return filters
              })()}
              hitsPerPage={60}
            />
            <AlgoliaSearchResults
              searchQuery={searchQuery}
              selectedAlbum={selectedAlbum}
              onClearAlbum={() => {
                setSelectedAlbum(null)
                setSelectedAlbumType(null)
              }}
            />
          </InstantSearch>
        ) : selectedAlbum ? (
          // When album is selected and not searching, show traditional assets
          <CategoryAssetView
            album={selectedAlbum}
            albumType={selectedAlbumType || assetType}
            onBack={() => {
              setSelectedAlbum(null)
              setSelectedAlbumType(null)
            }}
          />
        ) : // Default view: show categories using traditional API
        isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500">{(intl.loadingAssets as any)?.key || 'Loading...'}</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-red-500">
              {(intl.errorLoadingAssets as any)?.key || 'Error loading assets'}
            </div>
          </div>
        ) : (
          <CategoryBrowser
            assets={assets}
            assetType={assetType}
            onSelectCategory={(album, albumType) => {
              setSelectedAlbum(album)
              setSelectedAlbumType(albumType)
            }}
          />
        )}

        {/* Assistant popup */}
        {isOpen && (showAssistant || !hasSeenAssistant) && (
          <AssetLibraryAssistant
            onClose={() => {
              setShowAssistant(false)
              if (!hasSeenAssistant) {
                dismissAssistant()
              }
            }}
            isMobile={isMobile}
          />
        )}
      </div>
    </div>
  )
}
